/**
 * Een lint dat zich aanpast aan de breedte, zoals een Office-lint.
 *
 * `RibbonBar` meet hoeveel ruimte er is; elke `RibbonGroup` meet hoe breed hij
 * uitgeklapt en ingeklapt is. Past niet alles, dan klappen groepen in tot één
 * knop met een uitklapmenu, de minst belangrijke eerst (laagste `priority`).
 * Wordt het scherm weer breder, dan klappen ze in omgekeerde volgorde weer
 * uit. Zo vult de balk nooit meer dan één regel, ook op een smal scherm.
 *
 * De breedtes worden onthouden: een ingeklapte groep kent z'n uitgeklapte
 * breedte nog van de vorige keer, zodat de balk niet heen en weer springt.
 * Een groep met `priority={Infinity}` klapt nooit in.
 *
 * Is er meer ruimte dan de groepen nodig hebben, dan staan ze in het midden
 * (`innerClassName` met `mx-auto`). Anders dan `justify-center` valt er bij te
 * weinig ruimte dan links niets onbereikbaar buiten de scrollbare balk.
 */
import {
  createContext,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

type Mode = "full" | "compact";

interface Layout {
  collapsed: ReadonlySet<string>;
  report: (id: string, mode: Mode, width: number, priority: number) => void;
}

const LayoutContext = createContext<Layout | null>(null);

/** Wat een ingeklapte groep ongeveer inneemt, zolang die nog niet gemeten is. */
const COMPACT_GUESS = 72;
/** Speling, zodat de laatste groep niet tegen de rand plakt. */
const MARGIN = 8;

/**
 * Welke groepen inklappen bij deze beschikbare breedte: de laagste
 * prioriteit eerst, tot het past. Puur, zodat de regel apart te testen is.
 */
export function chooseCollapsed(
  groups: ReadonlyArray<{ id: string; full: number; compact?: number; priority: number }>,
  available: number,
): Set<string> {
  let total = groups.reduce((sum, g) => sum + g.full, 0);
  const out = new Set<string>();
  const order = [...groups]
    .filter((g) => Number.isFinite(g.priority))
    .sort((a, b) => a.priority - b.priority);
  for (const g of order) {
    if (total <= available) break;
    out.add(g.id);
    total -= g.full - (g.compact ?? COMPACT_GUESS);
  }
  return out;
}

export function RibbonBar({
  className = "",
  innerClassName = "",
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { innerClassName?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const widths = useRef(new Map<string, { full?: number; compact?: number; priority: number }>());
  const available = useRef(Number.POSITIVE_INFINITY);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());

  const recompute = useCallback(() => {
    const groups = [...widths.current].map(([id, w]) => ({ id, ...w }));
    if (groups.length === 0 || groups.some((g) => g.full === undefined)) return;
    const next = chooseCollapsed(
      groups as Array<{ id: string; full: number; compact?: number; priority: number }>,
      available.current - MARGIN,
    );
    setCollapsed((cur) =>
      cur.size === next.size && [...next].every((id) => cur.has(id)) ? cur : next,
    );
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      available.current = el.clientWidth;
      recompute();
    });
    observer.observe(el);
    available.current = el.clientWidth;
    return () => observer.disconnect();
  }, [recompute]);

  const layout = useMemo<Layout>(
    () => ({
      collapsed,
      report: (id, mode, width, priority) => {
        const cur = widths.current.get(id) ?? { priority };
        if (cur[mode] === width && cur.priority === priority) return;
        widths.current.set(id, { ...cur, [mode]: width, priority });
        recompute();
      },
    }),
    [collapsed, recompute],
  );

  return (
    <LayoutContext.Provider value={layout}>
      <div ref={ref} className={className} {...rest}>
        <div className={`mx-auto flex w-max ${innerClassName}`}>{children}</div>
      </div>
    </LayoutContext.Provider>
  );
}

export function RibbonGroup({
  id,
  label,
  shortLabel,
  priority,
  hidden = false,
  children,
}: {
  id: string;
  label: string;
  /** Het opschrift van de knop als de groep is ingeklapt. */
  shortLabel?: string;
  /** Hoe later inklappen, hoe hoger. `Infinity` = nooit. */
  priority: number;
  /** Niet tonen (bv. alleen kijken). De groep meet dan 0 breed, zodat de rest
   *  niet voor niets inklapt. */
  hidden?: boolean;
  children: ReactNode;
}) {
  const layout = useContext(LayoutContext);
  const isCollapsed = layout?.collapsed.has(id) ?? false;
  const ref = useRef<HTMLDivElement>(null);

  // Meten in de stand waarin de groep nu staat.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !layout) return;
    const mode: Mode = isCollapsed ? "compact" : "full";
    const measure = () => layout.report(id, mode, hidden ? 0 : el.offsetWidth, priority);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [layout, id, isCollapsed, priority, hidden]);

  if (hidden) return <div ref={ref} hidden />;
  if (isCollapsed) {
    return (
      <CollapsedGroup ref={ref} label={label} shortLabel={shortLabel ?? label}>
        {children}
      </CollapsedGroup>
    );
  }
  return (
    <div ref={ref} className="flex shrink-0 flex-col items-stretch px-2 pt-1 pb-0.5">
      <div className="flex flex-1 flex-col gap-0.5">{children}</div>
      <div className="mt-0.5 whitespace-nowrap text-center text-(--ld-muted) text-[11px] leading-none">
        {label}
      </div>
    </div>
  );
}

function CollapsedGroup({
  ref,
  label,
  shortLabel,
  children,
}: {
  ref: React.Ref<HTMLDivElement>;
  label: string;
  shortLabel: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Onder de knop, en binnen het scherm.
  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const place = () => {
      const r = button.current?.getBoundingClientRect();
      if (!r) return;
      const width = popover.current?.offsetWidth ?? 0;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      setPos({ top: r.bottom + 4, left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Dicht bij een klik ernaast of Escape. `pointerdown` in de capture-fase,
  // want het canvas (React Flow) houdt `mousedown` tegen. Een klik in een
  // kleurkiezer uit deze groep (eigen portal) telt niet als ernaast.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (!t) return;
      if (button.current?.contains(t) || popover.current?.contains(t)) return;
      if (t.closest?.("[data-swatch-popover]")) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="flex shrink-0 items-stretch">
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        title={label}
        className={`flex items-center gap-1 whitespace-nowrap px-2 py-1 text-xs hover:bg-(--ld-active) ${
          open ? "bg-(--ld-active)" : ""
        }`}
      >
        {shortLabel}
        <span aria-hidden="true" className="text-xs">
          ▾
        </span>
      </button>
      {open &&
        createPortal(
          <div
            ref={popover}
            role="group"
            aria-label={label}
            data-scope="toolbar"
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
            }}
            className="ledger z-[900] flex flex-col rounded-lg border border-(--ld-control) bg-(--ld-surface) px-2 pt-1.5 pb-1 text-(--ld-text) shadow-2xl"
          >
            <div className="flex flex-col gap-0.5">{children}</div>
            <div className="mt-1 text-center text-xs leading-none">{label}</div>
          </div>,
          document.body,
        )}
    </div>
  );
}
